// THE single source of truth for the raised player-building token look — the
// outline geometry AND the flat-shaded facet paint plan. Pure (no React, no
// Pixi), mirroring resourceShapes.ts: the board's canvas renderer
// (game/PixiGame.ts) and the rulebook's SVG building images both paint from the
// SAME spec, so a Scout / Barracks / Mine / home base looks identical on the
// board and in the manual.
//
// All geometry is centred at the origin (0,0) and scales with the radius `r`.

import type { Point } from "./resourceShapes";

/** Lighten/darken a colour by a multiplicative factor, clamped per channel. */
export function shade(base: number, factor: number): number {
  const r = (base >> 16) & 0xff;
  const g = (base >> 8) & 0xff;
  const b = base & 0xff;
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v * factor)));
  return (c(r) << 16) | (c(g) << 8) | c(b);
}

/** Outline polygon for a player building, centred at (0,0): barracks = upright
 * triangle, mine = square, house = home base (square body + triangle roof).
 * Scout returns `null` → drawn natively as a circle. Radii are nudged so the
 * shapes read at a similar visual weight. */
export function buildingOutline(kind: string, r: number): Point[] | null {
  switch (kind) {
    case "barracks": {
      const rr = r * 1.18; // a triangle looks small for its radius — bump it up
      return [
        [0, -rr],
        [rr * 0.866, rr * 0.5],
        [-rr * 0.866, rr * 0.5],
      ];
    }
    case "mine": {
      const s = r * 0.9; // half-side; a square reads heavy, so trim a touch
      return [
        [-s, -s],
        [s, -s],
        [s, s],
        [-s, s],
      ];
    }
    case "house": {
      // Home base: a house silhouette — square wall with a triangular roof.
      const s = r * 0.95;
      return [
        [-s, s * 0.9], // bottom-left
        [s, s * 0.9], // bottom-right
        [s, -s * 0.1], // right eave
        [0, -s * 1.15], // roof apex
        [-s, -s * 0.1], // left eave
      ];
    }
    default:
      return null; // scout → circle (disc)
  }
}

/** One paint step of a building token: a circle or polygon filled with the
 * owner colour shaded by `factor` (see `shade`), at optional `alpha`. */
export type BuildingTokenLayer = (
  | { shape: "circle"; cx: number; cy: number; r: number }
  | { shape: "poly"; points: Point[] }
) & { factor: number; alpha?: number };

export interface BuildingTokenSpec {
  /** Painted in order; fill = shade(ownerColor, factor). */
  layers: BuildingTokenLayer[];
  /** The thin white keyline stroked last, over the layers. */
  rim:
    | { shape: "circle"; cx: number; cy: number; r: number }
    | { shape: "poly"; points: Point[] };
}

/** The full paint plan for a building token (centred at 0,0) with simple
 * flat-shaded facets so it reads as a raised, 3D piece lit from the top-left —
 * unlike the resource icons, which stay flat 2D. The shape encodes the kind:
 * scout = circle (domed), barracks = triangle, mine = square, house = home base
 * (polygons chamfered into per-edge facets). */
export function buildingTokenSpec(kind: string, r: number): BuildingTokenSpec {
  const outline = buildingOutline(kind, r);
  const LX = -0.7071; // light direction (top-left)
  const LY = -0.7071;
  const ox = -r * 0.14; // top face / highlight nudged toward the light
  const oy = -r * 0.16;
  const shadowX = r * 0.1;
  const shadowY = r * 0.14;

  if (!outline) {
    // Scout: a domed disc — dark underside, mid body, lit cap toward the light.
    return {
      layers: [
        { shape: "circle", cx: shadowX, cy: shadowY, r, factor: 0.35, alpha: 0.42 },
        { shape: "circle", cx: 0, cy: 0, r, factor: 0.62 },
        { shape: "circle", cx: ox * 0.8, cy: oy * 0.8, r: r * 0.82, factor: 1 },
        { shape: "circle", cx: ox * 1.7, cy: oy * 1.7, r: r * 0.45, factor: 1.3 },
      ],
      rim: { shape: "circle", cx: 0, cy: 0, r },
    };
  }

  // Barracks / mine / house: chamfer into one flat facet per edge. The top face
  // is the outline scaled down and nudged toward the light; each facet fills the
  // strip from an outer edge to the matching top edge, shaded by how much that
  // edge faces the light, so the token looks beveled.
  const topScale = 0.58;
  const layers: BuildingTokenLayer[] = [
    {
      shape: "poly",
      points: outline.map(([x, y]): Point => [x + shadowX, y + shadowY]),
      factor: 0.35,
      alpha: 0.42,
    },
  ];
  const top = outline.map(
    ([x, y]): Point => [x * topScale + ox, y * topScale + oy],
  );
  const n = outline.length;
  for (let i = 0; i < n; i++) {
    const a = outline[i]!;
    const b = outline[(i + 1) % n]!;
    const ta = top[i]!;
    const tb = top[(i + 1) % n]!;
    const mx = (a[0] + b[0]) / 2; // edge midpoint ≈ outward normal direction
    const my = (a[1] + b[1]) / 2;
    const len = Math.hypot(mx, my) || 1;
    const lit = (mx / len) * LX + (my / len) * LY; // -1 (away) .. 1 (toward light)
    layers.push({
      shape: "poly",
      points: [a, b, tb, ta],
      factor: 0.75 + 0.28 * lit,
    });
  }
  layers.push({ shape: "poly", points: top, factor: 1.2 }); // lit top face
  return { layers, rim: { shape: "poly", points: outline } };
}
